<template>
  <div>
    <section class="shop-page py-9">
      <div class="container">
        <div class="row">

          <!-- Sidebar -->
          <aside class="col-md-3 mb-4">
            <div class="d-md-none mb-3">
              <button class="btn btn-filter-toggle w-100" 
                      type="button" 
                      data-bs-toggle="collapse" 
                      data-bs-target="#filterCollapse">
                <i class="fas fa-sliders-h me-2"></i> Filters
              </button>
            </div>

            <div class="collapse d-md-block text-start" id="filterCollapse">
              <div class="filter-box p-3 border rounded">
                <h5 class="mb-3">Categories</h5>
                <div v-for="category in categories" :key="category.slug" class="form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :id="category.slug"
                    :value="category.slug"
                    v-model="selectedCategories"
                    @change="getProducts"
                  />
                  <label class="form-check-label" :for="category.slug">
                    {{ category.title }}
                  </label>
                </div>

                <hr>
                <h5 class="mb-3">Sort By</h5>
                <select class="form-select" v-model="sortBy" @change="getProducts">
                  <option value="popularity">Popularity</option>
                  <option value="low_to_high">Price: Low to High</option>
                  <option value="high_to_low">Price: High to Low</option>
                  <option value="newest">Newest First</option>
                </select>
              </div>
            </div>
          </aside>

          <!-- Products -->
          <div class="col-md-9">
            <div class="row g-4">
              <div v-if="products.length === 0" class="col-12 text-center py-5">
                <h5 class="text-muted">No products available</h5>
              </div>

              <div v-else v-for="product in products" :key="product._id" class="col-6 col-md-4">
                <div class="card product-card position-relative">
                  <span
                    v-if="product.offerPrice"
                    class="position-absolute top-0 start-0 bg-danger text-white px-2 py-1 rounded-end"
                    style="font-size: 0.8rem;"
                  >
                    {{ product.offerPriceText || 'On Sale' }}
                  </span>
                  <a href="javascript:void(0)" @click="$router.push(`/product/${product.slug}`)" >
                    <img :src="product.mainImageUrl" class="card-img-top" :alt="product.name" />
                  </a>

                  <div class="card-body text-start">
                    <h6 class="card-title">{{ product.name }}</h6>

                    <!-- ✨ IMPROVED Price Display -->
                    <div class="product-price-block mb-2" :class="{ 'has-offer': product.offerPrice }">
                      <!-- When offer price exists -->
                      <template v-if="product.offerPrice">
                        <div class="price-row justify-content-left">
                          <span class="price-sale-current">₹{{ product.offerPrice }}</span>
                          <span class="price-was-label">was</span>
                          <span class="price-original-strike">₹{{ product.price }}</span>
                          <span class="price-savings-pill" v-if="product.price > product.offerPrice">
                            Save ₹{{ product.price - product.offerPrice }}
                          </span>
                        </div>
                      </template>
                      <!-- When no offer price -->
                      <template v-else>
                        <div class="price-row justify-content-left">
                          <span class="price-regular">₹{{ product.price }}</span>
                        </div>
                      </template>
                    </div>
                    <button @click="addProductToCart(product, true)" class="btn-add-cart">
                      <i class="fas fa-shopping-bag"></i>
                      <span>Buy Now</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useToast } from 'vue-toast-notification';
const $toast = useToast();
import ProductService from "../services/ProductService";
import { EventBus } from '@/eventBus'

export default {
  name: 'Shop',
  data() {
    return {
      categories: [],
      products: [],
      selectedCategories: [], // now stores slugs
      sortBy: 'popularity'
    };
  },
  methods: {
    getCategories() {
      ProductService.getCategories()
        .then(response => {
          this.categories = response.data.data;

          // Auto-select category from query using slug
          const categoryFromQuery = this.$route.query.category;
          if (categoryFromQuery) {
            const validCategory = this.categories.find(c => c.slug === categoryFromQuery);
            if (validCategory && !this.selectedCategories.includes(categoryFromQuery)) {
              this.selectedCategories.push(categoryFromQuery);
            }
          }

          this.getProducts();
        })
        .catch(e => {
          $toast.error(e.response?.data?.response_msg || e.message);
        });
    },

    getProducts() {
      // Get the slug from URL (single category)
      const categorySlugFromUrl = this.$route.params.slug;

      // Merge with selectedCategories checkboxes
      let categoriesArray = [...this.selectedCategories];

      // If URL has slug but it's not in selectedCategories, add it
      if (categorySlugFromUrl && !categoriesArray.includes(categorySlugFromUrl)) {
        categoriesArray.push(categorySlugFromUrl);
      }

      const data = {
        categories: categoriesArray,
        sortBy: this.sortBy
      };

      ProductService.shop(data)
        .then(response => {
          this.products = response.data.data;
        })
        .catch(e => {
          $toast.error(e.response?.data?.message || e.message);
        });
    },
    async addProductToCart(product, redirectToCartPage = false) {
      let data = {
        productId: product._id,
        productDetail: product,
        qty: 1,
      };

      await ProductService.addProductToCart(data)
        .then((response) => {
          EventBus.emit('cart-updated')
          $toast.success(response.data.respones_msg);
          if (redirectToCartPage) {
            this.$router.push({ path: '/cart' });
          }
        })
        .catch((e) => {
          $toast.error(e.response.data.respones_msg);
        });
    },
  },
  watch: {
    '$route.params.slug'(newSlug) {
      this.getProducts();
    }
  },
  created() {
    this.getCategories();
  }
};
</script>

<style scoped>
.py-9 {
  padding-top: 9rem !important;
  padding-bottom: 3rem !important;
}

/* ========== ✨ PRICE BLOCK ========== */
.product-price-block {
  margin-top: 6px;
  margin-bottom: 10px;
}

.price-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 5px;
}

.price-regular {
  font-size: 1.15rem;
  font-weight: 700;
  color: #2d3748;
  letter-spacing: -0.3px;
}

.price-sale-current {
  font-size: 1.2rem;
  font-weight: 800;
  color: #e53e3e;
  letter-spacing: -0.5px;
  line-height: 1;
  font-family: "New York", Iowan Old Style, Apple Garamond, Baskerville, Times New Roman, Droid Serif, Times, Source Serif Pro, serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol;
}

.price-was-label {
  font-size: 0.7rem;
  font-weight: 500;
  color: #a0aec0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  align-self: flex-end;
  padding-bottom: 2px;
}

.price-original-strike {
  font-size: 0.85rem;
  font-weight: 400;
  color: #a0aec0;
  text-decoration: line-through;
  text-decoration-color: #cbd5e0;
  align-self: flex-end;
  padding-bottom: 1px;
}

.price-savings-pill {
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, #fff5f5, #fed7d7);
  color: #c53030;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  border: 1px solid #feb2b2;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

@media (max-width: 480px) {
  .price-sale-current {
    font-size: 1.05rem;
  }

  .price-regular {
    font-size: 1rem;
  }

  .price-original-strike {
    font-size: 0.78rem;
  }

  .price-savings-pill {
    font-size: 0.6rem;
    padding: 2px 6px;
  }

  .price-was-label {
    font-size: 0.62rem;
  }
}

.btn-add-cart {
  width: 100%;
  padding: 12px;
  background: rgb(118, 172, 175);
  color: white;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  cursor: pointer;
  transition: all 0.3s;
}
</style>